import { Maybe } from 'types/graphql-apollo-v2';
import {
  RelationalCondition,
  RelationalRule,
  LogicalCondition,
  LogicalRule,
  parseFilterToLogicalRule,
  logicalRuleToFilter,
  isRelationalRuleComplete,
} from './rules';

export const defaultRelationalRule: RelationalRule = {
  _type: 'RelationalRule',
  condition: RelationalCondition.EQUALS,
  property: '',
  value: '',
  fieldType: 'String',
  isPropertyComparison: false,
};

export const defaultLogicalRule: LogicalRule = {
  _type: 'LogicalRule',
  condition: LogicalCondition.AND,
  children: [defaultRelationalRule],
};

export const emptyLogicalRule: LogicalRule = {
  _type: 'LogicalRule',
  condition: LogicalCondition.AND,
  children: [],
};

export const defaultLogicalRuleFilter: string = JSON.stringify(
  logicalRuleToFilter(defaultLogicalRule, [])
);

export const emptyLogicalRuleFilter: string = JSON.stringify({
  and: [],
});

/** Count every leaf relational rule in a logical tree, including nested groups. */
export const countRelationalRules = (rule: LogicalRule): number =>
  rule.children.reduce(
    (acc, child) =>
      acc +
      (child._type === 'RelationalRule' ? 1 : countRelationalRules(child)),
    0
  );

/**
 * True when the tree contains a RelationalRule that would be dropped by
 * logicalRuleToFilter (missing property, or missing value for a condition
 * that requires one). Used to block Save in filter-builder wizards so users
 * can't silently lose conditions they thought they entered.
 */
export const hasIncompleteRelationalRule = (rule: LogicalRule): boolean =>
  rule.children.some((child) =>
    child._type === 'RelationalRule'
      ? !isRelationalRuleComplete(child)
      : hasIncompleteRelationalRule(child)
  );

/** Gets all the rules from inside a conditions filter string. */
export const getRelationalConditionsFromString = (
  conditionsString: Maybe<string> | undefined
): RelationalRule[] => {
  if (!conditionsString) return [];

  const parsedTree = parseFilterToLogicalRule(conditionsString);
  if (!parsedTree) return [];

  return getRelationalConditionsFromTree(parsedTree);
};

// Gets all the Relational Rules from inside a tree
const getRelationalConditionsFromTree = (
  tree: LogicalRule
): RelationalRule[] => {
  let relationalConditions: RelationalRule[] = [];
  tree.children.forEach((gr) => {
    if (gr._type === 'LogicalRule') {
      relationalConditions = [
        ...relationalConditions,
        ...getRelationalConditionsFromTree(gr).flat(),
      ];
    }
    if (gr._type === 'RelationalRule') {
      relationalConditions = [...relationalConditions, gr];
    }
  });

  return [...relationalConditions];
};
